import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  ExperienceDto,
  GetRedirectsByExperienceIdResponseBodyType,
} from "@intelligems/ig-types";
import { useQuery } from "@tanstack/react-query";

import {
  initRedirectsState,
  initUrlRedirectsState,
  selectedRedirectsState,
} from "../../store/slices/redirects";
import { selectedVariations } from "../../store/slices/variations";
import { IExperienceState } from "../../store/types/experiences";
import { api } from "../api";
import { useGetOrg } from "../organizations/get-organization";
import { useGetShopDomain } from "../shops/get-domain";

export const redirectKeys = {
  all: [{ scope: "redirects" }] as const,
  byExperience: (experienceId: string) =>
    [{ scope: "redirects", experienceId }] as const,
};
export const getRedirectsByExperienceId = async (
  experienceId: string
): Promise<GetRedirectsByExperienceIdResponseBodyType> => {
  const resp = await api.get<GetRedirectsByExperienceIdResponseBodyType>(
    `/v2/redirects/experience/${experienceId}`
  );
  return resp.data;
};
export const useRedirectsByExperienceId = (
  experienceId: string | undefined
) => {
  return useQuery<GetRedirectsByExperienceIdResponseBodyType, Error>({
    queryKey: redirectKeys.byExperience(experienceId!),
    queryFn: async () => getRedirectsByExperienceId(experienceId!),
    enabled: Boolean(experienceId),
  });
};
export const useAndSetRedirectsByExperienceId = (
  experienceState: IExperienceState
) => {
  const dispatch = useDispatch();
  const redirectState = useSelector(selectedRedirectsState);
  const variations = useSelector(selectedVariations);
  const domain = useGetShopDomain();
  const org = useGetOrg();

  const redirectsQuery = useRedirectsByExperienceId(
    experienceState.experience?.id
  );

  useEffect(() => {
    if (experienceState.isSuccess && experienceState?.experience) {
      if (
        (experienceState.experience.actionMode === "edit" &&
          redirectsQuery.isSuccess &&
          domain.isSuccess) || // have this second check here for when switching between analytics and details quickly
        (redirectsQuery.isSuccess &&
          redirectsQuery.data.redirects.length &&
          domain.isSuccess)
      ) {
        dispatch(
          initRedirectsState({
            redirects: redirectsQuery.data.redirects,
            domain: domain.data ?? org.data.org.shopId!,
          })
        );
      } else if (
        redirectState.redirects.length === 0 &&
        experienceState.experience.type === "content/url" &&
        experienceState.experience.actionMode === "create" &&
        variations.length > 0
      ) {
        // initialize url redirect
        dispatch(
          initUrlRedirectsState({
            variations,
          })
        );
      }
    }
  }, [
    experienceState.isSuccess,
    experienceState?.experience?.type,
    variations,
    redirectsQuery.isSuccess,
    domain.isSuccess,
  ]);

  return redirectState;
};
